/**
 * ErrorBoundary.tsx
 * Reusable React error boundary (v1.0.09).
 *
 * WHY: the app had NO error boundary, so a single render throw anywhere in a
 * page unmounted the entire React root — a completely blank white screen (the
 * "blank Scenario Planning page" bug: a closed popover's sentinel weekStart
 * crashed one cell and took the whole SPA down). This boundary converts any
 * future render/lifecycle throw in its subtree into a CONTAINED panel with a
 * reload button, while the rest of the app (app bar, side nav) keeps working.
 *
 * Usage: wrap the narrowest sensible scope — e.g. SchedulePage wraps its
 * <Routes> so a crashing schedule view leaves the schedule side-nav usable.
 * Pass a `resetKey` (e.g. the current pathname) so navigating away from the
 * crashed view automatically clears the error and re-renders the new view.
 *
 * Class component by necessity: error boundaries require getDerivedStateFromError
 * / componentDidCatch, which have no hook equivalent. Styling uses theme tokens
 * via MUI `sx` (no hex literals, per CODING-STANDARDS).
 */

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * When this value changes (e.g. the route pathname), a caught error is
   * cleared and the subtree re-renders — so navigating to another view
   * recovers without a full page reload.
   */
  resetKey?: string;
  /** Optional context label shown in the panel ("this page" by default). */
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log for the feedback console-capture pipeline (errorCapture reads console).
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Route (or other resetKey) changed while showing the error → try the new
    // subtree fresh instead of trapping the user on the crash panel.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private handleTryAgain = (): void => {
    this.setState({ error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;

    const where = this.props.label ?? 'this page';
    return (
      <Box
        sx={{
          m: 2,
          p: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          textAlign: 'center',
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: '2.5rem', color: 'error.main' }} />
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Something went wrong on {where}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          The rest of the app is still working. You can try again, reload, or
          navigate to another page. If this keeps happening, please report it
          via the feedback button.
        </Typography>
        {/* Technical detail — small, for the bug report screenshot. */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontFamily: 'monospace', maxWidth: 480, wordBreak: 'break-word' }}
        >
          {this.state.error.message}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant="outlined" size="small" onClick={this.handleTryAgain} sx={{ textTransform: 'none' }}>
            Try again
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={this.handleReload}
            sx={{ textTransform: 'none' }}
          >
            Reload page
          </Button>
        </Box>
      </Box>
    );
  }
}

export default ErrorBoundary;
